///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Pointer drag on the right dock's rail items: drag up/down to reorder
/// the docked windows, drag left off the rail to float the window at the
/// pointer. Order changes stay local until release (one reorder dispatch);
/// a short press with no travel is left to the item's click handler.
///

import { reorderDrawerTabs, undockTabToWindow, type DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { useCallback, useEffect, useRef, useState } from "react"

// Travel before a press becomes a drag
const DRAG_THRESHOLD = 5
// Horizontal travel left of the rail that floats the window on release
const FLOAT_THRESHOLD = 60

export interface RailDragState {
	windowId: string
	x: number
	y: number
	floating: boolean
}

export function useRailDrag(tabOrder: string[], datasetWindows: Record<string, DatasetBrowserWindowState>) {
	const dispatch = useAppDispatch()
	const itemElsRef = useRef<Record<string, HTMLElement | null>>({})
	const [drag, setDrag] = useState<RailDragState | null>(null)
	const [localOrder, setLocalOrder] = useState<string[] | null>(null)
	const startRef = useRef<{ x: number; y: number } | null>(null)
	const pressedIdRef = useRef<string | null>(null)
	const suppressClickRef = useRef(false)

	const registerItem = useCallback((windowId: string, el: HTMLElement | null) => {
		itemElsRef.current[windowId] = el
	}, [])

	const handlePointerDown = useCallback((windowId: string, e: React.PointerEvent) => {
		if (e.button !== 0) return
		const win = datasetWindows[windowId]
		if (!win) return
		pressedIdRef.current = windowId
		startRef.current = { x: e.clientX, y: e.clientY }
		suppressClickRef.current = false
	}, [datasetWindows])

	// Swallow the click that follows a drag so release doesn't also select
	const shouldSuppressClick = useCallback(() => {
		const suppress = suppressClickRef.current
		suppressClickRef.current = false
		return suppress
	}, [])

	useEffect(() => {
		const onMove = (e: PointerEvent) => {
			const pressed = pressedIdRef.current
			const start = startRef.current
			if (!pressed || !start) return

			const dx = e.clientX - start.x
			const dy = e.clientY - start.y
			if (!drag) {
				if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return
				suppressClickRef.current = true
				setLocalOrder(tabOrder)
			}

			const railEl = itemElsRef.current[pressed]
			const railLeft = railEl?.parentElement?.getBoundingClientRect().left ?? start.x
			const floating = datasetWindows[pressed]?.dockMode !== "dock-only" && e.clientX < railLeft - FLOAT_THRESHOLD
			setDrag({ windowId: pressed, x: e.clientX, y: e.clientY, floating })

			if (floating) return
			setLocalOrder(order => {
				const base = order ?? tabOrder
				const without = base.filter(id => id !== pressed)
				let insertAt = without.length
				for (let i = 0; i < without.length; i++) {
					const el = itemElsRef.current[without[i]]
					if (!el) continue
					const rect = el.getBoundingClientRect()
					if (e.clientY < rect.top + rect.height / 2) {
						insertAt = i
						break
					}
				}
				const next = [...without.slice(0, insertAt), pressed, ...without.slice(insertAt)]
				return next.every((id, i) => id === base[i]) ? base : next
			})
		}

		const onUp = (e: PointerEvent) => {
			const pressed = pressedIdRef.current
			pressedIdRef.current = null
			startRef.current = null
			if (!pressed || !drag) {
				setDrag(null)
				setLocalOrder(null)
				return
			}

			if (drag.floating) {
				const win = datasetWindows[pressed]
				const width = win?.width || 600
				const height = win?.height || 400
				dispatch(undockTabToWindow({
					windowId: pressed,
					x: Math.max(0, Math.min(e.clientX - width / 2, window.innerWidth - width)),
					y: Math.max(0, Math.min(e.clientY - 20, window.innerHeight - height - 50)),
				}))
			} else if (localOrder) {
				const fromIndex = tabOrder.indexOf(pressed)
				const toIndex = localOrder.indexOf(pressed)
				if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
					dispatch(reorderDrawerTabs({ fromIndex, toIndex }))
				}
			}
			setDrag(null)
			setLocalOrder(null)
		}

		document.addEventListener("pointermove", onMove)
		document.addEventListener("pointerup", onUp)
		return () => {
			document.removeEventListener("pointermove", onMove)
			document.removeEventListener("pointerup", onUp)
		}
	}, [drag, localOrder, tabOrder, datasetWindows, dispatch])

	return { drag, localOrder, registerItem, handlePointerDown, shouldSuppressClick }
}
